import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useSwipeable} from 'react-swipeable';
import {requestContext} from '../../../../../../common/requestContext';
import useResizeObserver from '../../../../../../common/shared/hooks/useResizeObserver';
import {preventDefaultEvent} from '../../../../../../common/shared/utils/preventDefaultEvent.function';
import {twMerge} from '../../../../../../common/twMerge';
import {useAddWindowEventListener} from '../../../../../../common/useAddWindowEventListener.hook';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {IconType} from '../../../../../../design-system/atoms/Icon/IconType.enum';
import {getInternalAssetImage} from '../../../../../../design-system/atoms/StaticImage/getInternalAssetImage.function';
import {STATIC_IMAGE_INFO_MAP} from '../../../../../../design-system/atoms/StaticImage/StaticImageInfoMap.constant';
import {
  CONTENTS_MOBILE_MAX_WIDTH,
  CONTENTS_PC_MAX_HEIGHT,
  PAGE_TEXT_VIEWER_CONTENTS_PC_MAX_WIDTH,
  PAGE_TEXT_VIEWER_CONTENTS_PC_MIN_WIDTH,
  PAGE_TEXT_VIEWER_CONTENTS_PC_TWO_PAGE_MAX_WIDTH,
  TEXT_VIEWER_DARK_THEME_LIST,
} from '../constant/viewerConstant';
import {useCheckTextViewerNeedsLoadContent} from '../hook/useCheckTextViewerNeedsLoadContent.hook';
import {useTextViewerCoreOption} from '../hook/useTextViewerCoreOption.hook';
import {useTextViewerPageHandler} from '../hook/useTextViewerPageHandler.hook';
import {useViewerMenuHandler} from '../hook/useViewerMenuHandler';
import {HtmlPageViewer} from '../presentational/HtmlPageViewer';
import {TextViewerPageCounter} from '../presentational/TextViewerPageCounter';
import {FrameSize} from '../type/FrameSize.interface';
import {getPageSubtotalCount} from '../util/getPageSubtotalCount';
import {PageTextViewerProgressContainer} from './PageTextViewerProgressContainer';

export function PageTextViewerContainer({
  seriesId,
  productId,
  isCmsPreview,
  children,
}: {
  seriesId: number;
  productId: number;
  isCmsPreview: boolean;
  children?: ReactNode;
}) {
  const {isMobile} = requestContext.requestInfo.display;

  const [frameSize, setFrameSize] = useState<FrameSize>({
    width: 0,
    height: 0,
  });
  const {ref: viewerWrapperRef} = useResizeObserver<HTMLDivElement>({
    onResize: ({width, height}) => {
      if (!width || !height) {
        return;
      }
      setFrameSize({width, height});
    },
  });

  useCheckTextViewerNeedsLoadContent({productId, size: frameSize});

  const {htmlInfo, currentPage, currentChapterId, chapterCount} =
    useTypedSelector(state => ({
      htmlInfo: state.textViewer.viewData[productId]?.currentHtmlInfo,
      currentPage: state.textViewer.viewData[productId]?.currentPage,
      currentChapterId: state.textViewer.viewData[productId]?.currentChapterId,
      chapterCount: state.textViewer.viewData[productId]?.chapterCount,
    }));

  const option = useTextViewerCoreOption({size: frameSize, isMobile});
  const {goToPrevPageHandler, goToNextPageHandler} = useTextViewerPageHandler({
    productId,
    option,
  });

  const {shouldUseNonstopWatching} = useViewerMenuHandler({
    isMobile,
  });

  const swipeableHandlers = useSwipeable({
    trackMouse: true,
    onSwipedLeft: ({event}) => {
      goToNextPageHandler(event instanceof MouseEvent);
    },
    onSwipedRight: ({event}) => {
      goToPrevPageHandler(event instanceof MouseEvent);
    },
  });

  const rootDivElementRef = useRef<HTMLDivElement | null>(null);

  const refPassthrough = (element: HTMLDivElement | null) => {
    swipeableHandlers.ref(element);
    rootDivElementRef.current = element;
  };

  const onTabScreen = useCallback(
    (event: MouseEvent) => {
      if (!isMobile) {
        return;
      }
      const {clientX} = event;
      const element = event.target as HTMLElement;
      const {left, width} = element.getBoundingClientRect();
      if (left + width * 0.25 >= clientX) {
        goToPrevPageHandler();
        event.stopImmediatePropagation();
      } else if (left + width * 0.75 <= clientX) {
        goToNextPageHandler();
        event.stopImmediatePropagation();
      }
    },
    [goToNextPageHandler, goToPrevPageHandler, isMobile]
  );

  useAddWindowEventListener({
    type: 'click',
    element: rootDivElementRef.current,
    listener: onTabScreen,
  });

  const onArrowKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.repeat) return;
      // Edge, 파폭 구버전에서 Left, Right로 들어온다고 그 code 값도 추가했습니다.
      // https://stackoverflow.com/questions/5597060/detecting-arrow-key-presses-in-javascript#comment91057577_44213036
      if (e.code === 'ArrowLeft' || e.code === 'Left') {
        goToPrevPageHandler();
      } else if (e.code === 'ArrowRight' || e.code === 'Right') {
        goToNextPageHandler();
      }
    },
    [goToNextPageHandler, goToPrevPageHandler]
  );

  useAddWindowEventListener<any>({
    type: 'keydown',
    listener: onArrowKeyDown,
  });

  useEffect(() => {
    if (!chapterCount || currentChapterId === undefined) {
      return;
    }
    // 다음 회차가 있는 상태의 정주행 모드로 변경될 때는 컨텐츠의 마지막 페이지가 보이도록 조정합니다.
    if (shouldUseNonstopWatching) {
      const lastChapterId = chapterCount - 1;
      if (currentChapterId > lastChapterId) {
        goToPrevPageHandler();
      }
    }
  }, [
    chapterCount,
    currentChapterId,
    goToPrevPageHandler,
    shouldUseNonstopWatching,
  ]);

  const {isViewerEnd, chapterPageCountList, isDarkColor} = useTypedSelector(
    state => ({
      isViewerEnd: state.textViewer.viewData[productId]?.isViewerEnd,
      chapterPageCountList:
        state.textViewer.viewData[productId]?.chapterPageCountList,
      isDarkColor: TEXT_VIEWER_DARK_THEME_LIST.includes(
        state.textViewer.settings.theme
      ),
    })
  );

  const pageSubtotalCount = useMemo(() => {
    if (currentPage === undefined || currentChapterId === undefined) return -1;
    return getPageSubtotalCount({
      currentChapterId,
      currentPage,
      isViewerEnd,
      chapterPageCountList,
    });
  }, [chapterPageCountList, currentChapterId, currentPage, isViewerEnd]);

  const leftImageInfo = STATIC_IMAGE_INFO_MAP[IconType.ViewerArrowLeft];
  const rightImageInfo = STATIC_IMAGE_INFO_MAP[IconType.ViewerArrowRight];

  return (
    <div
      className={twMerge(
        'mx-auto flex h-full items-center justify-center',
        !isMobile && 'py-[10vh]'
      )}
      style={{
        backgroundColor: isViewerEnd ? undefined : option.backgroundColor,
      }}
      {...swipeableHandlers}
      ref={refPassthrough}
      onSelect={preventDefaultEvent}
      onContextMenu={preventDefaultEvent}
    >
      {!isMobile && (
        <div
          onClick={() => goToPrevPageHandler(true)}
          className={twMerge(
            'flex w-112pxr shrink-0 cursor-pointer items-center justify-start',
            pageSubtotalCount === 1 && 'pointer-events-none opacity-30'
          )}
        >
          <img
            className={twMerge('p-30pxr', !isViewerEnd && 'active:opacity-30')}
            alt={leftImageInfo.alt}
            src={getInternalAssetImage(
              (isDarkColor ? leftImageInfo.srcDark : leftImageInfo.srcLight) ||
                ''
            )}
            draggable={false}
          />
        </div>
      )}
      <div
        ref={viewerWrapperRef}
        className="h-full w-full flex-auto"
        style={{
          minWidth: !isMobile
            ? PAGE_TEXT_VIEWER_CONTENTS_PC_MIN_WIDTH
            : undefined,
          maxWidth: isMobile
            ? CONTENTS_MOBILE_MAX_WIDTH
            : option.isTwoColumnLayout
              ? PAGE_TEXT_VIEWER_CONTENTS_PC_TWO_PAGE_MAX_WIDTH
              : PAGE_TEXT_VIEWER_CONTENTS_PC_MAX_WIDTH,
        }}
      >
        {htmlInfo && currentPage !== undefined && !isViewerEnd && (
          <HtmlPageViewer
            htmlInfo={htmlInfo}
            option={option}
            pageIndex={currentPage - 1}
            productId={productId}
          />
        )}
        {!shouldUseNonstopWatching &&
          !!isViewerEnd &&
          (isMobile ? (
            <div className="flex min-h-full items-center">{children}</div>
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <div
                className="flex h-full w-full max-w-720pxr items-center bg-theme-bg-surface-alt"
                style={{
                  maxHeight: !isMobile ? CONTENTS_PC_MAX_HEIGHT : undefined,
                }}
              >
                {children}
              </div>
            </div>
          ))}
      </div>
      {!isMobile && (
        <div
          onClick={() => goToNextPageHandler(true)}
          className={twMerge(
            'flex w-112pxr shrink-0 cursor-pointer items-center justify-end',
            isViewerEnd && 'pointer-events-none opacity-30'
          )}
        >
          <img
            className={twMerge('p-30pxr', !isViewerEnd && 'active:opacity-30')}
            alt={rightImageInfo.alt}
            src={getInternalAssetImage(
              (isDarkColor
                ? rightImageInfo.srcDark
                : rightImageInfo.srcLight) || ''
            )}
            draggable={false}
          />
        </div>
      )}
      <PageTextViewerProgressContainer
        seriesId={seriesId}
        productId={productId}
        isCmsPreview={isCmsPreview}
      />
      <TextViewerPageCounter productId={productId} size={frameSize} />
    </div>
  );
}
